import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

import { ApiResponse } from "@/api/rest/core/types";
import {
  EntityConfiguration,
  EntityConfigurationEntityType,
  EntityConfigurationType,
  useUpdateConfiguration,
} from "@/api/rest/services/configurationService";
import { UserInfo } from "@/api/rest/models/account";
import { ApiBanner } from "@/components/common/Banner";
import Button from "@/components/common/Button";
import TextArea from "@/components/common/TextArea";
import Field from "@/components/common/Field";
import { isDobuEmployee } from "@/utils/userUtils";

interface AISettingsProps {
  buildingConfiguration: EntityConfiguration[];
  buildingId: number;
  refreshBuildingConfiguration: () => void;
}

type AIFormData = {
  [EntityConfigurationType.PROPERTY_MANAGER_CASE_PROMPT]: string;
  [EntityConfigurationType.QUESTIONS_PROMPT]: string;
  [EntityConfigurationType.HOUSEHOLD_IMAGES_PATTERN]: string;
  [EntityConfigurationType.VOICE_WARRANTY_PROMPT]: string;
};

const VOICE_PROMPT_REQUIRED_VARS = ["{{householdContext}}", "{{categories}}"] as const;

const DEFAULT_VOICE_PROMPT_TEMPLATE = `You are a helpful voice warranty assistant for DoBu, a homeowner's app.

## Property Information:
{{householdContext}}

## Available Warranty Categories:
{{categories}}

## Conversation Flow:
1. Greet the homeowner warmly
2. Ask what issue they're experiencing
3. Categorize the issue into the appropriate warranty category
4. Use your knowledge base to check if the issue is likely covered under warranty
5. Gather key details: location in the home, when it was noticed, what exactly is wrong
6. Summarize and confirm the details before submitting
7. Submit the warranty case

## Voice-Specific Rules:
- Keep responses concise and conversational - this is a phone call, not a chat
- Ask only ONE question at a time
- Do not use markdown formatting, bullet points, or numbered lists in your speech
- Use natural spoken language
- If the homeowner is unclear, ask a simple clarifying question
- Confirm details by summarizing back to the homeowner
- Be empathetic and professional`;

type FieldErrorState = Set<EntityConfigurationType>;

type ValidationErrors = Partial<Record<EntityConfigurationType, string>>;

export function AISettings({ buildingConfiguration, buildingId, refreshBuildingConfiguration }: AISettingsProps) {
  const { user } = useOutletContext<{ user: UserInfo }>();
  const { t } = useTranslation();
  const { mutateAsync: updateConfiguration } = useUpdateConfiguration();
  const [saveResult, setSaveResult] = useState<ApiResponse<unknown> | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorState>(new Set());
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const getConfigValue = useCallback(
    (type: EntityConfigurationType): string => {
      const config = buildingConfiguration?.find((conf) => conf.type === type);
      return config?.value || "";
    },
    [buildingConfiguration],
  );

  const initialFormData = useMemo<AIFormData>(
    () => ({
      [EntityConfigurationType.PROPERTY_MANAGER_CASE_PROMPT]: getConfigValue(
        EntityConfigurationType.PROPERTY_MANAGER_CASE_PROMPT,
      ),
      [EntityConfigurationType.QUESTIONS_PROMPT]: getConfigValue(EntityConfigurationType.QUESTIONS_PROMPT),
      [EntityConfigurationType.HOUSEHOLD_IMAGES_PATTERN]: getConfigValue(
        EntityConfigurationType.HOUSEHOLD_IMAGES_PATTERN,
      ),
      [EntityConfigurationType.VOICE_WARRANTY_PROMPT]: getConfigValue(EntityConfigurationType.VOICE_WARRANTY_PROMPT),
    }),
    [getConfigValue],
  );

  const [formData, setFormData] = useState<AIFormData>(initialFormData);

  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  const handleInputChange = (configurationType: EntityConfigurationType, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [configurationType]: value,
    }));
    // Clear error for this field when user edits it
    if (fieldErrors.has(configurationType)) {
      setFieldErrors((prev) => {
        const newErrors = new Set(prev);
        newErrors.delete(configurationType);
        return newErrors;
      });
    }
    if (validationErrors[configurationType]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[configurationType];
        return next;
      });
    }
  };

  const handleSave = useCallback(async () => {
    setSaveResult(null);
    setFieldErrors(new Set());
    setValidationErrors({});

    // Validate voice prompt if it has content
    if (isDobuEmployee(user)) {
      const voicePrompt = formData[EntityConfigurationType.VOICE_WARRANTY_PROMPT];
      if (voicePrompt.trim()) {
        const missing = VOICE_PROMPT_REQUIRED_VARS.filter((v) => !voicePrompt.includes(v));
        if (missing.length > 0) {
          setValidationErrors({
            [EntityConfigurationType.VOICE_WARRANTY_PROMPT]: `Missing required variables: ${missing.join(", ")}`,
          });
          return;
        }
      }
    }

    setIsSaving(true);

    const updates: Array<{
      type: EntityConfigurationType;
      value: string;
    }> = [
      {
        type: EntityConfigurationType.PROPERTY_MANAGER_CASE_PROMPT,
        value: formData[EntityConfigurationType.PROPERTY_MANAGER_CASE_PROMPT],
      },
    ];

    if (isDobuEmployee(user)) {
      updates.push(
        { type: EntityConfigurationType.QUESTIONS_PROMPT, value: formData[EntityConfigurationType.QUESTIONS_PROMPT] },
        {
          type: EntityConfigurationType.HOUSEHOLD_IMAGES_PATTERN,
          value: formData[EntityConfigurationType.HOUSEHOLD_IMAGES_PATTERN],
        },
        {
          type: EntityConfigurationType.VOICE_WARRANTY_PROMPT,
          value: formData[EntityConfigurationType.VOICE_WARRANTY_PROMPT],
        },
      );
    }

    const results = await Promise.allSettled(
      updates.map((update) =>
        updateConfiguration({
          entityType: EntityConfigurationEntityType.BUILDING,
          buildingId: buildingId,
          configurationType: update.type,
          configurationValue: update.value,
        }).catch((error) => {
          console.error(`Failed to update ${update.type}:`, error);
          throw error;
        }),
      ),
    );

    const newFieldErrors = new Set<EntityConfigurationType>();
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        newFieldErrors.add(updates[index].type);
      }
    });

    const allSuccess = results.every((result) => result.status === "fulfilled");

    setIsSaving(false);
    setFieldErrors(newFieldErrors);

    if (allSuccess) {
      setSaveResult({
        success: true,
        result: null,
      });
      refreshBuildingConfiguration();
    } else {
      setSaveResult({
        success: false,
        result: undefined,
        message: t("buildingSettings.ai.saveError"),
      });
    }

    setTimeout(() => setSaveResult(null), 10000);
  }, [formData, buildingId, updateConfiguration, t, refreshBuildingConfiguration, user]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <Field
          hasError={fieldErrors.has(EntityConfigurationType.PROPERTY_MANAGER_CASE_PROMPT)}
          id="property-manager-case-prompt"
        >
          <Field.Label>{t("building.propertyManagerCaseRules")}</Field.Label>
          <TextArea
            value={formData[EntityConfigurationType.PROPERTY_MANAGER_CASE_PROMPT]}
            onChange={(e) => handleInputChange(EntityConfigurationType.PROPERTY_MANAGER_CASE_PROMPT, e.target.value)}
            disabled={isSaving}
            rows={6}
          />
          <Field.Error>{t("buildingSettings.fieldSaveError")}</Field.Error>
        </Field>

        {isDobuEmployee(user) && (
          <Field hasError={fieldErrors.has(EntityConfigurationType.QUESTIONS_PROMPT)} id="questions-prompt">
            <Field.Label>{t("building.questionsPrompt")}</Field.Label>
            <TextArea
              value={formData[EntityConfigurationType.QUESTIONS_PROMPT]}
              onChange={(e) => handleInputChange(EntityConfigurationType.QUESTIONS_PROMPT, e.target.value)}
              disabled={isSaving}
              rows={6}
            />
            <Field.Error>{t("buildingSettings.fieldSaveError")}</Field.Error>
          </Field>
        )}

        {isDobuEmployee(user) && (
          <Field
            hasError={fieldErrors.has(EntityConfigurationType.HOUSEHOLD_IMAGES_PATTERN)}
            id="household-images-pattern"
          >
            <Field.Label>{t("building.householdImagesPattern")}</Field.Label>
            <TextArea
              value={formData[EntityConfigurationType.HOUSEHOLD_IMAGES_PATTERN]}
              onChange={(e) => handleInputChange(EntityConfigurationType.HOUSEHOLD_IMAGES_PATTERN, e.target.value)}
              disabled={isSaving}
              rows={6}
            />
            <Field.Error>{t("buildingSettings.fieldSaveError")}</Field.Error>
          </Field>
        )}

        {isDobuEmployee(user) && (
          <Field
            hasError={
              fieldErrors.has(EntityConfigurationType.VOICE_WARRANTY_PROMPT) ||
              Boolean(validationErrors[EntityConfigurationType.VOICE_WARRANTY_PROMPT])
            }
            id="voice-warranty-prompt"
          >
            <Field.Label>{t("building.voiceWarrantyPrompt")}</Field.Label>
            <p className="text-xs text-gray-400">
              {"Available variables: {{householdContext}}, {{categories}}. Leave empty to use the default prompt."}
            </p>
            <TextArea
              value={formData[EntityConfigurationType.VOICE_WARRANTY_PROMPT]}
              onChange={(e) => handleInputChange(EntityConfigurationType.VOICE_WARRANTY_PROMPT, e.target.value)}
              placeholder={DEFAULT_VOICE_PROMPT_TEMPLATE}
              disabled={isSaving}
              rows={10}
            />
            {validationErrors[EntityConfigurationType.VOICE_WARRANTY_PROMPT] && (
              <p className="text-sm text-red-600">{validationErrors[EntityConfigurationType.VOICE_WARRANTY_PROMPT]}</p>
            )}
            <Field.Error>{t("buildingSettings.fieldSaveError")}</Field.Error>
          </Field>
        )}

        <div className="flex items-center gap-4">
          <Button onClick={handleSave} disabled={isSaving} loading={isSaving}>
            {isSaving ? t("settings.saving") : t("settings.save")}
          </Button>
          <div className="flex-grow">
            {saveResult && <ApiBanner apiResponse={saveResult} successMessage={t("buildingSettings.ai.saveSuccess")} />}
          </div>
        </div>
      </div>
    </div>
  );
}
